<template>
  <!-- @vue-ignore TODO: unclear types on datepicker since Vue 3.4 & Vite 5 upgrade. -->
  <date-picker
    v-bind="$attrs"
    v-model:value="localDate"
    :class="{
      'fs-date-picker': true,
      'fs-date-picker--inline': inline,
      'fs-date-picker--striped': striped,
      'fs-date-picker--small': size === 'small',
      'fs-date-picker--no-outline': !outline,
    }"
    :popup-class="`fs-datepicker__popup ${inline ? 'fs-datepicker__popup--inline' : ''}`"
    :append-to-body="!inline"
    :open="inline || $attrs.open"
    :disabled-date="disabledDate"
    :clearable="clearable"
    type="date"
    placeholder="Select date"
    :lang="langObject"
    :highlights="highlights"
    show-week-number
    @change="onChange"
    @pick="onPick"
  />
</template>

<script setup lang="ts">
import DatePicker from 'vue-datepicker-next';
import { computed } from 'vue';
import { dateToDateOnlyString, parseDateOnlyString } from '@lib/formatters';

interface Props {
  modelValue?: Date | string | Date[];
  outline?: boolean;
  inline?: boolean;
  striped?: boolean;
  valueType?: 'date' | 'string';
  size?: 'small' | 'default';
  /** Shows a cross that clears the date. Clearing emits `clear`, not an empty model value. */
  clearable?: boolean;
  highlights?: Record<
    string /* date to be highlighted, e.g. 2024-12-10 */,
    { highlighted: boolean; name?: string; period?: string }
  >;
  disabledDate?: ((value: Date, innerValue?: Date[] | undefined) => boolean) | undefined;
}

const props = withDefaults(defineProps<Props>(), {
  outline: true,
  inline: false,
  size: 'default',
  valueType: 'date',
  clearable: false,
});

const emits = defineEmits<{
  'update:modelValue': [value: Date | string];
  clear: [];
  change: [value: any];
  pick: [value: any];
}>();

const localDate = computed<Date | undefined>({
  get() {
    if (!props.modelValue) return;

    return props.valueType === 'string' ? parseDateOnlyString(props.modelValue as string) : (props.modelValue as Date);
  },
  set(value: Date | null | undefined) {
    if (!value) {
      if (props.clearable) emits('clear');
      return;
    }

    emits('update:modelValue', valueFromDate(value));
  },
});

const onChange = (value: Date | null) => {
  emits('change', valueFromDate(value));
};
const onPick = (value: Date | null) => {
  emits('pick', valueFromDate(value));
};

function valueFromDate(date: Date): Date | string;
function valueFromDate(date: Date | null | undefined): Date | string | undefined;
function valueFromDate(date: Date | null | undefined) {
  if (!date) return undefined;
  return props.valueType === 'string' ? dateToDateOnlyString(date) : date;
}

const langObject = {
  formatLocale: {
    firstDayOfWeek: 1,
  },
  // the calendar header, default formatLocale.weekdaysMin
  days: ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'],
  yearFormat: 'YYYY',
  monthFormat: 'MMM',
};
</script>

<style lang="scss">
// Due to popup being teleported, styling for non-inline version needs to be added globally in datepicker.scss
@import '@styles/datepicker';
@import '@styles/variables';

.fs-datepicker__popup {
  z-index: $zi-datepicker;

  &--inline {
    z-index: $popup-z-index;
  }
}
</style>

<style lang="scss" scoped>
.fs-date-picker {
  // Styling to override default one for 'inline' version

  &--inline {
    width: auto;

    :deep(.mx-datepicker-main) {
      position: relative !important;
      left: 0 !important;
      top: 0 !important;
      box-shadow: none !important;
    }

    :deep(.mx-input-wrapper) {
      display: none;
    }
  }

  &--no-outline {
    :deep(.mx-datepicker-main) {
      border: none;
    }
  }

  &--striped {
    :deep(.mx-table-date td.active) {
      background-image: repeating-linear-gradient(
        -45deg,
        rgba(255, 255, 255, 0.15) 0,
        rgba(255, 255, 255, 0.15) 4px,
        rgba(0, 0, 0, 0) 4px,
        rgba(0, 0, 0, 0) 8px
      );
    }
  }

  &--small {
    // Override styles in datepicker.scss
    :deep(.mx-calendar) {
      width: 28rem;
      padding: 1.2rem 1rem 0.4rem;
    }

    :deep(.mx-table-date) {
      width: 26rem;
    }

    :deep(.mx-btn-text) {
      font-size: 1.8rem;
    }

    :deep(.mx-icon-right),
    :deep(.mx-icon-left) {
      transform: scale(0.8);
    }

    :deep(.mx-table th) {
      font-size: 1.2rem !important;
      line-height: 1.4rem !important;
      font-weight: 400;
    }

    :deep(.mx-week-number div) {
      font-size: 1rem;
      line-height: 1.2rem;
    }

    :deep(.mx-table-date td) {
      font-size: 1.4rem;
      line-height: 1.7rem;
    }

    :deep(.mx-calendar-header) {
      margin-bottom: 0;
    }

    :deep(.mx-calendar-content) {
      height: 23rem;
    }
  }
}
</style>
